import {Door, DoorMessageLevel, FullGroup} from '@shared/models/api_models';

import {createDataStore} from '@shared_web/lib/data_store';
import {createMapStore} from '@shared_web/lib/map_data_store';
import {notifyError} from '@shared_web/lib/notification';

import {apiCall} from '@mavismart_frontend/lib/api';
import {getAccount} from '@mavismart_frontend/stores/account_store';

//
// ALL DOORS STORE
//

interface AllDoors {
  groups: FullGroup[];
  doors: Map<string, Door>;
}

const allDoorsStore = createDataStore<AllDoors | undefined>(undefined);

let isFetching = false;

export const useAllDoors = () => {
  fetchAllDoorsIfNeeded();
  return allDoorsStore.useData();
};

export function fetchAllDoorsIfNeeded() {
  // isFetching flag to prevent multiple simultaneous fetches
  // Check account to avoid fetching when not logged in
  // Check allDoorsStore to avoid fetching if we already have the data
  if (!isFetching && getAccount() !== undefined && allDoorsStore.getData() === undefined) {
    isFetching = true;
    apiCall('POST /list-all-doors', {})
      .then(async response => {
        const allDoors: AllDoors = {
          groups: response.groups,
          doors: new Map(response.doors.map(door => [door.id, door])),
        };
        allDoorsStore.setData(allDoors);
        isFetching = false;
      })
      .catch(notifyError);
  }
}

export const useAllDoorsNoFetch = () => allDoorsStore.useData();

export const clearAllDoors = () => {
  allDoorsStore.setData(undefined);
  doorStore.batchUnsetData([...doorStore.getAllData().keys()]);
};

//
// DOOR STORE
//
const doorStore = createMapStore<string, Door>();

export const setDoor = doorStore.setData;

export function useDoor(doorId?: string): Door | undefined {
  const door = doorStore.useData(doorId);
  const allDoors = useAllDoorsNoFetch();

  if (!doorId) {
    return door;
  }
  return door ?? allDoors?.doors.get(doorId);
}

export function fetchDoor(doorId: string) {
  apiCall('POST /get-door', {id: doorId})
    .then(door => {
      doorStore.setData(doorId, door);
      // Also update the allDoorsStore if we have it
      const current = allDoorsStore.getData();
      if (current) {
        current.doors.set(doorId, door);
        allDoorsStore.setData({...current});
      }
    })
    .catch(notifyError);
}

export function updateDoorStatus(doorId: string, newStatus: DoorMessageLevel) {
  // Check if status has changed
  const currentDoor = doorStore.getData(doorId);
  if (!currentDoor || currentDoor.status === newStatus) {
    return;
  }
  // Update the doorStore
  currentDoor.status = newStatus;
  doorStore.setData(doorId, {...currentDoor});
  // Update the allDoorsStore
  const current = allDoorsStore.getData();
  if (!current) return;
  current.doors.set(doorId, {...currentDoor});
  allDoorsStore.setData({...current});
}

//
// UTILS
//

export const updateDoorLabel = (doorId: string, newLabel: string) => {
  // Update the allDoorsStore
  const current = allDoorsStore.getData();
  if (!current) return;
  const doors = new Map(current.doors);
  const door = doors.get(doorId);
  if (door) {
    doors.set(doorId, {...door, label: newLabel});
    allDoorsStore.setData({...current, doors});
  }
  // Update the doorStore
  const currentDoor = doorStore.getData(doorId);
  if (currentDoor) {
    doorStore.setData(doorId, {...currentDoor, label: newLabel});
  }
};

export const updateDoorCustomAddress = (doorId: string, newCustomAddress: string | undefined) => {
  // Update the allDoorsStore
  const current = allDoorsStore.getData();
  if (!current) return;
  const doors = new Map(current.doors);
  const door = doors.get(doorId);
  if (door) {
    doors.set(doorId, {...door, customAddress: newCustomAddress});
    allDoorsStore.setData({...current, doors});
  }
  // Update the doorStore
  const currentDoor = doorStore.getData(doorId);
  if (currentDoor) {
    doorStore.setData(doorId, {...currentDoor, customAddress: newCustomAddress});
  }
};
